'use client';

import useUser from '@shared/utils/hooks/useUser';

import { client } from '../../utils/trpc';
import { type ArcadeGameSlug, bestKey } from './funGames';
import { recordBest } from './useLocalBest';

/**
 * One place for "the player finished a round".
 *
 * Two destinations, deliberately:
 *  - **localStorage**, always. This is what makes the arcade work logged out and
 *    offline, and it is what the hub cards read.
 *  - **`GameStat` on the server**, only when signed in, so history survives a
 *    device change.
 *
 * The server write is fire-and-forget. A failed sync must never cost the player
 * the score they just earned or block the UI — the local best is already
 * committed by then, so the worst case is that this run is missing from their
 * cross-device history.
 */

/** Games where a *lower* number is the better result. */
const LOWER_IS_BETTER: ReadonlySet<ArcadeGameSlug> = new Set(['spend']);

export const isBetterScore = (
  game: ArcadeGameSlug,
  next: number,
  previous: number
) => (LOWER_IS_BETTER.has(game) ? next < previous : next > previous);

export const useGameTracking = () => {
  const { currentUser } = useUser();
  const signedIn = Boolean(currentUser?.id);

  const record = (
    game: ArcadeGameSlug,
    score: number,
    detail?: Record<string, string | number>
  ) => {
    recordBest(
      bestKey(game),
      score,
      LOWER_IS_BETTER.has(game) ? 'lower' : 'higher'
    );

    if (!signedIn) return;

    /* Not awaited: the round is already over and the local best is saved, so
     * there is nothing for the player to wait on.
     *
     * `silenceErrors` matters more than it looks. The tRPC client toasts every
     * error by default, so without it a failed background sync throws an error
     * toast over the game the player is still in the middle of — telemetry must
     * never interrupt play. */
    void client.arcade.recordStat.mutate(
      { game, best: score, detail },
      { context: { silenceErrors: true } }
    );
  };

  return { record, signedIn };
};
